import * as React from "react";
import { useEffect, useRef, useMemo, useState, Suspense } from "react";
import { DateTime } from "luxon";
import Typography from "@mui/material/Typography";
import styled from "@emotion/styled";
import { Button, Card, CardActions, CardContent, IconButton, Link, Menu, MenuItem, Tooltip } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import CancelIcon from "@mui/icons-material/Cancel";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  getEnvironmentStageData,
  isProdEnvironment,
  isScalableEnvironment,
} from "../../../services/registry/EnvironmentService";
import { EnvironmentState, IEnvironment } from "../../../types/EnvironmentTypes";
import { EnvironmentStateIndicator } from "./EnvironmentStateIndicator";
import { DeploymentLogSelector } from "../../../components/DeploymentLogSelector";
import { IBackendImage } from "../../../services/registry/BackendImageService";
import { IServerImage } from "../../../services/registry/ServerImageService";
import { getBackendImageIdLink, getServerImageIdLink } from "../../../TfidLinks";
import { IRegistryFilesMetadata, useRegistryService } from "../../../services/registry/RegistryService";
import { getRegistryURL } from "../../../services/registry/Registry";
import { YesNoCancelPopup } from "../../../components/YesNoCancelPopup";
import { LoadingOverlay } from "../../../components/LoadingOverlay";
import { CopyIconButton } from "../../../components/CopyIconButton";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { FeaturesLoader, clearFeaturesCache } from "./FeaturesLoader";
import { FeatureItem } from "../../../utils/Features";
import { AgentTokenPopup } from "./AgentTokenPopup";

const DraggableCard = styled(Card)`
  position: absolute;
  z-index: 1000;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
`;

const DragHandle = styled.div`
  position: absolute;
  top: 12px;
  left: 12px;
  cursor: move;
  color: rgba(255, 255, 255, 0.7);
  z-index: 1001;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: rgba(255, 255, 255, 1);
  }
`;

const EnvironmentCardHeader = ({ stage }: { stage: number }) => {
  const [backgroundColor, labelColor, label] = useMemo(() => {
    return getEnvironmentStageData(stage);
  }, [stage]);
  return (
    <div style={{ height: "70px", paddingRight: "55px", backgroundColor, overflow: "hidden", position: "relative" }}>
      <Typography gutterBottom variant="h1" component="div" color={labelColor} textAlign="right">
        <b>{label}</b>
      </Typography>
    </div>
  );
};

const EnvironmentCardTitle = styled.div`
  display: flex;
  flex: 1;
  flex-direction: row;
  align-items: flex-start;
  justify-content: space-between;
  height: 44px;
`;

const EnvironmentCardLine = styled.div`
  display: flex;
  flex: 1;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  height: 32px;
`;

export interface EnvironmentCardProps {
  content: IEnvironment;
  backendImages: IBackendImage[];
  serverImages: IServerImage[];
  onCloseButtonClick: () => void;
  onEditButtonClick: () => void;
  onTerminateButtonClick: () => void;
  onReviveButtonClick: () => void;
  onSetDefaultButtonClick: () => void;
  onDeployBackendImageButtonClick: () => void;
  onChangeActivePackageButtonClick: () => void;
  onChangeActiveSchemaBranchButtonClick: () => void;
  onChangeActiveSchemaVersionButtonClick: () => void;
  onShowGameServersButtonClick: () => void;
  onChangeServerImageBranchButtonClick: () => void;
  onChangeServerImageIdButtonClick: () => void;
  onShowDeploymentLogButtonClick: (logName: string) => void;
  onShowAllLogsButtonClick: () => void;
  onViewActivePackageButtonClick: () => void;
  onViewActiveSchemaBranchButtonClick: () => void;
  onViewActiveSchemaVersionButtonClick: () => void;
  onViewServerImageBranchButtonClick: () => void;
  onViewServerImageIdButtonClick: () => void;
  onResetButtonClick: () => void;
  onReplicateFromButtonClick: () => void;
  onChangeInstanceTypeButtonClick: () => void;
  onRestartInstanceButtonClick: () => void;
  onStartStopInstanceButtonClick: () => void;
  onSoftRestartButtonClick: () => void;
  onEditFeaturesButtonClick: () => void;
  onSchedulerButtonClick: () => void;
  onViewCloudWatchDashboardsButtonClick: () => void;
  onCreateCloudWatchDashboardButtonClick: () => void;
  onShowMetricsButtonClick: () => void;
  onShowDockerStatusButtonClick: () => void;
  onClearSDCacheButtonClick: () => void;
  onOverrideEnvironmentVariablesButtonClick: () => void;
}

const ENV_CARD_POSITION_STORAGE_KEY = "environment-card-position";

interface EnvironmentCardPosition {
  x: number;
  y: number;
}

export const EnvironmentCard = ({
  content,
  backendImages,
  serverImages,
  onCloseButtonClick,
  onEditButtonClick,
  onTerminateButtonClick,
  onReviveButtonClick,
  onSetDefaultButtonClick,
  onDeployBackendImageButtonClick,
  onChangeActivePackageButtonClick,
  onChangeActiveSchemaBranchButtonClick,
  onChangeActiveSchemaVersionButtonClick,
  onShowGameServersButtonClick,
  onChangeServerImageBranchButtonClick,
  onChangeServerImageIdButtonClick,
  onShowDeploymentLogButtonClick,
  onShowAllLogsButtonClick,
  onViewActivePackageButtonClick,
  onViewActiveSchemaBranchButtonClick,
  onViewActiveSchemaVersionButtonClick,
  onViewServerImageBranchButtonClick,
  onViewServerImageIdButtonClick,
  onResetButtonClick,
  onReplicateFromButtonClick,
  onChangeInstanceTypeButtonClick,
  onRestartInstanceButtonClick,
  onStartStopInstanceButtonClick,
  onSoftRestartButtonClick,
  onEditFeaturesButtonClick,
  onSchedulerButtonClick,
  onViewCloudWatchDashboardsButtonClick,
  onCreateCloudWatchDashboardButtonClick,
  onShowMetricsButtonClick,
  onShowDockerStatusButtonClick,
  onClearSDCacheButtonClick,
  onOverrideEnvironmentVariablesButtonClick,
}: EnvironmentCardProps) => {
  const [selectedDeploymentLog, setSelectedDeploymentLog] = useState<string | undefined>(undefined);
  const cardRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<EnvironmentCardPosition | undefined>(undefined);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [anchorManageMenuEl, setAnchorManageMenuEl] = React.useState<null | HTMLElement>(null);
  const [filesMetadata, setFilesMetadata] = useState<IRegistryFilesMetadata | undefined>(undefined);
  const [devFilesMetadata, setDevFilesMetadata] = useState<IRegistryFilesMetadata | undefined>(undefined);
  const [syncConfirmVisible, setSyncConfirmVisible] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [debugErrorResponsesEnabled, setDebugErrorResponsesEnabled] = useState(false);
  const [agentTokenPopupVisible, setAgentTokenPopupVisible] = useState(false);

  const registryService = useRegistryService();

  useEffect(() => {
    const isScalable = isScalableEnvironment(content.stage);
    if (isScalable) {
      // Fetch metadata from the registry of the selected environment
      registryService
        .getFilesMetadata(content.registryURL)
        .then((metadata) => setFilesMetadata(metadata))
        .catch((err) => console.error("Failed to fetch files metadata from registry of the selected environment:", err));

      // Fetch metadata from the DEV registry
      registryService
        .getFilesMetadata(getRegistryURL())
        .then((metadata) => setDevFilesMetadata(metadata))
        .catch((err) => console.error("Failed to fetch files metadata from the DEV registry:", err));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content.registryURL, content.stage]);

  useEffect(() => {
    // Clear features cache when environment changes
    clearFeaturesCache();
    setDebugErrorResponsesEnabled(false);
  }, [content.name]);

  const handleFeaturesLoaded = (features: FeatureItem[]) => {
    const debugFeature = features.find((f) => f.key === "debug-error-responses-enabled");
    if (debugFeature && debugFeature.currentValue === true) {
      setDebugErrorResponsesEnabled(true);
    } else {
      setDebugErrorResponsesEnabled(false);
    }
  };

  const handleFeaturesError = () => {
    setDebugErrorResponsesEnabled(false);
  };

  const open = Boolean(anchorManageMenuEl);

  const handleOpenManageMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorManageMenuEl(event.currentTarget);
  };

  const handleCloseManageMenuClick = () => {
    setAnchorManageMenuEl(null);
  };

  const menuItemClickedHandler = (action: () => void) => {
    handleCloseManageMenuClick();
    action();
  };

  useEffect(() => {
    const savedPosition = localStorage.getItem(ENV_CARD_POSITION_STORAGE_KEY);
    if (savedPosition) {
      try {
        const parsed = JSON.parse(savedPosition);
        setPosition(parsed);
      } catch (e) {
        console.error("Failed to parse saved position:", e);
      }
    } else {
      // Center the card if no saved position in localStorage
      if (cardRef.current && typeof window !== "undefined") {
        const { width, height } = cardRef.current.getBoundingClientRect();
        const centerX = (window.innerWidth - width) / 2;
        const centerY = (window.innerHeight - height) / 3;
        setPosition({ x: centerX, y: centerY });
      }
    }
  }, []);

  useEffect(() => {
    if (position) {
      localStorage.setItem(ENV_CARD_POSITION_STORAGE_KEY, JSON.stringify(position));
    }
  }, [position]);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (cardRef.current) {
      const { left, top } = cardRef.current.getBoundingClientRect();
      setDragOffset({
        x: e.clientX - left,
        y: e.clientY - top,
      });
      setIsDragging(true);
    }
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (isDragging) {
      const x = e.clientX - dragOffset.x;
      const y = e.clientY - dragOffset.y;
      setPosition({ x, y });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
    } else {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    }

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDragging]);

  const creationDate = DateTime.fromSeconds(content.creationDate).toLocaleString(DateTime.DATETIME_FULL_WITH_SECONDS);
  const terminationDate = content.terminationDate
    ? DateTime.fromSeconds(content.terminationDate).toLocaleString(DateTime.DATETIME_FULL_WITH_SECONDS)
    : "-";
  const upTime = (content.terminationDate ?? Date.now() / 1000) - content.creationDate; // in seconds
  const estimatedCost = (upTime / 3600) * 0.0168; // TODO: assuming t4g.small here, get that information from our backend for more accuracy
  const cannotDeploy =
    content.terminationDate !== undefined ||
    (content.state !== EnvironmentState.HEALTHY &&
      content.state !== EnvironmentState.RUNNING &&
      content.state !== EnvironmentState.UNKNOWN &&
      content.state !== EnvironmentState.UNHEALTHY);
  const cannotEditOrTerminate =
    content.terminationDate !== undefined ||
    content.state === EnvironmentState.REFRESHING ||
    content.state === EnvironmentState.TERMINATED ||
    content.state === EnvironmentState.PENDING_TERMINATION;
  const cannotRevive = content.terminationDate === undefined || content.state !== EnvironmentState.TERMINATED;
  const cannotOpenDockerStatus =
    content.state === EnvironmentState.STOPPED ||
    content.state === EnvironmentState.TERMINATED ||
    content.state === EnvironmentState.REFRESHING;
  const isProd = isProdEnvironment(content.stage);
  const isScalable = isScalableEnvironment(content.stage);
  let activeSchemaVersion = "NOT SET";
  if (content.activeSchemaVersion !== undefined) {
    if (content.activeSchemaVersion > 0) {
      activeSchemaVersion = content.activeSchemaVersion.toString();
    } else {
      activeSchemaVersion = "latest";
    }
  }

  const handleSyncAllButtonClick = () => {
    setSyncConfirmVisible(true);
  };

  const handleSyncConfirmNo = () => {
    setSyncConfirmVisible(false);
  };

  const handleSyncConfirmYes = async () => {
    setSyncConfirmVisible(false);
    setSyncing(true);
    try {
      await registryService.syncFiles(content.registryURL);
      // Refresh metadata after sync
      const [metadata, devMetadata] = await Promise.all([
        registryService.getFilesMetadata(content.registryURL),
        registryService.getFilesMetadata(getRegistryURL()),
      ]);
      setFilesMetadata(metadata);
      setDevFilesMetadata(devMetadata);
    } catch (err) {
      console.error("Failed to sync files:", err);
    } finally {
      setSyncing(false);
    }
  };

  const formatMetadataValue = (version: number | undefined, updateTime: number | undefined) => {
    if (version === undefined || updateTime === undefined) return "Loading...";
    const dateTime = DateTime.fromSeconds(updateTime).toLocaleString(DateTime.DATETIME_SHORT);
    return `${version} - ${dateTime}`;
  };

  return (
    <DraggableCard
      ref={cardRef}
      style={{
        left: `${position?.x}px`,
        top: `${position?.y}px`,
        width: "1000px",
        cursor: isDragging ? "grabbing" : "auto",
      }}
    >
      <DragHandle onMouseDown={handleMouseDown}>
        <DragIndicatorIcon />
      </DragHandle>
      <EnvironmentCardHeader stage={content.stage} />
      <CardContent>
        <EnvironmentCardTitle>
          <Typography gutterBottom variant="h5" component="div">
            <b>{content.name + (content.isDefault ? " (default)" : "")}</b>
          </Typography>
          <EnvironmentStateIndicator
            state={content.state || EnvironmentState.UNKNOWN}
            showLabel={true}
            style={{ marginTop: 3 }}
          />
        </EnvironmentCardTitle>
        <div
          style={{ display: "flex", flex: 1, flexDirection: "row", alignItems: "center", justifyContent: "space-between" }}
        >
          {content.previous !== undefined && <Button size="small">Show previous</Button>}
          <div style={{ display: "flex", flex: 1, flexDirection: "column" }}>
            <Typography variant="body2" color="text.secondary" textAlign="right" style={{ marginBottom: 2 }}>
              {creationDate}
            </Typography>
            <Typography variant="body2" color="text.secondary" textAlign="right" style={{ marginBottom: 2 }}>
              {terminationDate}
            </Typography>
          </div>
        </div>
        {!isScalable && (
          <Typography variant="body2" color="text.secondary" textAlign="right" style={{ marginBottom: 8 }}>
            Estimated cost: <b>${estimatedCost.toFixed(2)}</b> (USD)
          </Typography>
        )}
        {!isScalable && (
          <EnvironmentCardLine>
            <Typography variant="body1" color="text.secondary">
              Instance Type: <b>{content.instanceType || "t4g.small"}</b>
            </Typography>
            <div style={{ display: "flex", flex: 1, flexDirection: "row", justifyContent: "flex-end" }}>
              <Button variant="outlined" size="small" endIcon={<ExpandMoreIcon />} onClick={handleOpenManageMenuClick}>
                Manage Instance
              </Button>
              <Menu id="basic-menu" anchorEl={anchorManageMenuEl} open={open} onClose={handleCloseManageMenuClick}>
                <MenuItem onClick={() => menuItemClickedHandler(onSchedulerButtonClick)} disabled={content.isDefault}>
                  Auto-stop Schedule
                </MenuItem>
                <MenuItem
                  onClick={() => menuItemClickedHandler(onOverrideEnvironmentVariablesButtonClick)}
                  disabled={cannotEditOrTerminate}
                >
                  Override Environment Variables
                </MenuItem>
                <MenuItem
                  onClick={() => menuItemClickedHandler(onStartStopInstanceButtonClick)}
                  disabled={cannotEditOrTerminate}
                >
                  {content.state === EnvironmentState.STOPPED ? "Start" : "Stop"}
                </MenuItem>
                <MenuItem
                  onClick={() => menuItemClickedHandler(onRestartInstanceButtonClick)}
                  disabled={cannotEditOrTerminate}
                >
                  Restart
                </MenuItem>
                <MenuItem onClick={() => menuItemClickedHandler(onSoftRestartButtonClick)} disabled={cannotEditOrTerminate}>
                  Soft Restart
                </MenuItem>
                <MenuItem
                  onClick={() => menuItemClickedHandler(onChangeInstanceTypeButtonClick)}
                  disabled={cannotEditOrTerminate}
                >
                  Change
                </MenuItem>
              </Menu>
            </div>
          </EnvironmentCardLine>
        )}
        <EnvironmentCardLine>
          <Typography variant="body1" color="text.secondary">
            Base URL: <b>{content.baseURL}</b>
          </Typography>
          <Button size="small" onClick={() => setAgentTokenPopupVisible(true)}>
            Agent Token
          </Button>
        </EnvironmentCardLine>
        <EnvironmentCardLine>
          <Typography variant="body1" color="text.secondary">
            Backend Image ID:{" "}
            <b>{content.backendImageId ? getBackendImageIdLink(content.backendImageId, backendImages) : "NOT SET"}</b>
          </Typography>
          <Button size="small" disabled={cannotDeploy} onClick={onDeployBackendImageButtonClick}>
            Deploy
          </Button>
        </EnvironmentCardLine>
        <EnvironmentCardLine>
          <Typography variant="body1" color="text.secondary">
            Active Package ID:{" "}
            <b>
              {content.activePackageId ? (
                <Link href={`/static-data?pkg=${content.activePackageId}`} target="_blank" rel="noopener noreferrer">
                  {content.activePackageId}
                </Link>
              ) : (
                "NOT SET"
              )}
            </b>
          </Typography>
          <div style={{ display: "flex", flex: 1, flexDirection: "row", justifyContent: "flex-end" }}>
            <Button
              size="small"
              disabled={cannotDeploy}
              startIcon={<OpenInNewIcon />}
              onClick={onViewActivePackageButtonClick}
            >
              View
            </Button>
            <Button
              size="small"
              disabled={cannotDeploy}
              onClick={onChangeActivePackageButtonClick}
              style={{ marginLeft: 8 }}
            >
              Change
            </Button>
          </div>
        </EnvironmentCardLine>
        <EnvironmentCardLine>
          <Typography variant="body1" color="text.secondary">
            Active Schema Branch: <b>{content.activeSchemaBranch ?? "NOT SET"}</b>
          </Typography>
          <div style={{ display: "flex", flex: 1, flexDirection: "row", justifyContent: "flex-end" }}>
            <Button
              size="small"
              disabled={cannotDeploy}
              startIcon={<OpenInNewIcon />}
              onClick={onViewActiveSchemaBranchButtonClick}
            >
              View
            </Button>
            <Button
              size="small"
              disabled={cannotDeploy}
              onClick={onChangeActiveSchemaBranchButtonClick}
              style={{ marginLeft: 8 }}
            >
              Change
            </Button>
          </div>
        </EnvironmentCardLine>
        <EnvironmentCardLine>
          <Typography variant="body1" color="text.secondary">
            Active Schema Version: <b>{activeSchemaVersion}</b>
          </Typography>
          <div style={{ display: "flex", flex: 1, flexDirection: "row", justifyContent: "flex-end" }}>
            <Button
              size="small"
              disabled={cannotDeploy}
              startIcon={<OpenInNewIcon />}
              onClick={onViewActiveSchemaVersionButtonClick}
            >
              View
            </Button>
            <Button
              size="small"
              disabled={cannotDeploy}
              onClick={onChangeActiveSchemaVersionButtonClick}
              style={{ marginLeft: 8 }}
            >
              Change
            </Button>
          </div>
        </EnvironmentCardLine>
        <EnvironmentCardLine>
          <Typography variant="body1" color="text.secondary">
            Server Image Branch: <b>{content.serverImageBranch || "default"}</b>
          </Typography>
          {content.serverImageId ? (
            <div style={{ display: "flex", flex: 1, flexDirection: "row", justifyContent: "flex-start", paddingLeft: 2 }}>
              <Tooltip title="Server Image ID overrides the Server Image Branch: when both are set, only the Server Image ID is used">
                <WarningAmberIcon color="warning" />
              </Tooltip>
            </div>
          ) : null}
          <div style={{ display: "flex", flex: 1, flexDirection: "row", justifyContent: "flex-end" }}>
            <Button
              size="small"
              disabled={cannotDeploy}
              startIcon={<OpenInNewIcon />}
              onClick={onViewServerImageBranchButtonClick}
            >
              View
            </Button>
            <Button
              size="small"
              disabled={cannotDeploy}
              onClick={onChangeServerImageBranchButtonClick}
              style={{ marginLeft: 8 }}
            >
              Change
            </Button>
          </div>
        </EnvironmentCardLine>
        <EnvironmentCardLine>
          <Typography variant="body1" color="text.secondary">
            Server Image ID:{" "}
            <b>{content.serverImageId ? getServerImageIdLink(content.serverImageId, serverImages) : "default"}</b>
          </Typography>
          <div style={{ display: "flex", flex: 1, flexDirection: "row", justifyContent: "flex-end" }}>
            <Button
              size="small"
              disabled={cannotDeploy}
              startIcon={<OpenInNewIcon />}
              onClick={onViewServerImageIdButtonClick}
            >
              View
            </Button>
            <Button
              size="small"
              disabled={cannotDeploy}
              onClick={onChangeServerImageIdButtonClick}
              style={{ marginLeft: 8 }}
            >
              Change
            </Button>
          </div>
        </EnvironmentCardLine>
        <EnvironmentCardLine>
          <div style={{ flexGrow: 1 }} />
          <Button size="small" disabled={cannotDeploy} onClick={onEditFeaturesButtonClick} style={{ marginTop: 4 }}>
            Edit other features
          </Button>
        </EnvironmentCardLine>
        <Suspense fallback={null}>
          <FeaturesLoader environment={content} onFeaturesLoaded={handleFeaturesLoaded} onError={handleFeaturesError} />
        </Suspense>
        {debugErrorResponsesEnabled && (
          <EnvironmentCardLine>
            <WarningAmberIcon color="warning" style={{ marginRight: 8 }} />
            <Typography variant="body1" color="warning.main">
              <b>Warning:</b> Debug error responses are enabled. This should be disabled as soon as possible.
            </Typography>
          </EnvironmentCardLine>
        )}
        {isScalable ? (
          <>
            <EnvironmentCardLine>
              <Typography variant="body1" color="text.secondary">
                Registry File Version:{" "}
                <b>
                  {formatMetadataValue(filesMetadata?.registryFileVersion, filesMetadata?.registryFileUpdateTime)} (on dev:{" "}
                  {formatMetadataValue(devFilesMetadata?.registryFileVersion, devFilesMetadata?.registryFileUpdateTime)})
                </b>
              </Typography>
            </EnvironmentCardLine>
            <EnvironmentCardLine>
              <Typography variant="body1" color="text.secondary">
                Compiled Data Schema Version:{" "}
                <b>
                  {formatMetadataValue(
                    filesMetadata?.compiledDataSchemaVersion,
                    filesMetadata?.compiledDataSchemaUpdateTime,
                  )}{" "}
                  (on dev:{" "}
                  {formatMetadataValue(
                    devFilesMetadata?.compiledDataSchemaVersion,
                    devFilesMetadata?.compiledDataSchemaUpdateTime,
                  )}
                  )
                </b>
              </Typography>
            </EnvironmentCardLine>
            <EnvironmentCardLine>
              <Typography variant="body1" color="text.secondary">
                TFID Types File Version:{" "}
                <b>
                  {formatMetadataValue(filesMetadata?.tfidTypesFileVersion, filesMetadata?.tfidTypesFileUpdateTime)} (on dev:{" "}
                  {formatMetadataValue(devFilesMetadata?.tfidTypesFileVersion, devFilesMetadata?.tfidTypesFileUpdateTime)})
                </b>
              </Typography>
            </EnvironmentCardLine>
            <EnvironmentCardLine>
              <div style={{ flexGrow: 1 }} />
              <Button size="small" onClick={handleSyncAllButtonClick} style={{ marginTop: 4 }}>
                Sync all
              </Button>
            </EnvironmentCardLine>
          </>
        ) : null}
        {!isScalable && content.publicIpAddress !== undefined ? (
          <EnvironmentCardLine>
            <Typography variant="body1" color="text.secondary">
              Public IP Address: <b>{content.publicIpAddress}</b>
            </Typography>
          </EnvironmentCardLine>
        ) : null}
        {!isScalable && content.privateIpAddress !== undefined ? (
          <EnvironmentCardLine>
            <Typography variant="body1" color="text.secondary">
              Private IP Address: <b>{content.privateIpAddress}</b>
              <CopyIconButton text={content.privateIpAddress || ""} alertMessage="Private IP address copied" />
            </Typography>
          </EnvironmentCardLine>
        ) : null}
        <EnvironmentCardLine>
          <Typography variant="body1" color="text.secondary">
            Game-servers: <b>{content.gameServerCount ?? "0"}</b>
          </Typography>
          <Button size="small" onClick={onShowGameServersButtonClick}>
            Show
          </Button>
        </EnvironmentCardLine>
        {!isScalable && (
          <EnvironmentCardLine style={{ marginTop: 16 }}>
            <DeploymentLogSelector
              id="12"
              deploymentLogs={content.deploymentLogs || []}
              selectedDeploymentLog={selectedDeploymentLog}
              onDeploymentLogSelected={setSelectedDeploymentLog}
            />
            <Button
              size="small"
              onClick={() => onShowDeploymentLogButtonClick(selectedDeploymentLog || "")}
              disabled={!selectedDeploymentLog}
            >
              Show
            </Button>
          </EnvironmentCardLine>
        )}
        <EnvironmentCardLine style={{ marginTop: 8 }}>
          <div style={{ display: "flex", flexGrow: 1 }} />
          {!isScalable && (
            <Button
              size="small"
              startIcon={<OpenInNewIcon />}
              onClick={onViewCloudWatchDashboardsButtonClick}
              style={{ marginTop: 4 }}
            >
              View CloudWatch dashboards
            </Button>
          )}
          {!isScalable && (
            <Button size="small" onClick={onCreateCloudWatchDashboardButtonClick} style={{ marginTop: 4 }}>
              Create CloudWatch dashboard
            </Button>
          )}
          <Button size="small" onClick={onShowMetricsButtonClick} style={{ marginTop: 4, marginLeft: 8 }}>
            Show Metrics
          </Button>
          {!isScalableEnvironment(content.stage as number) && (
            <Button
              size="small"
              onClick={onShowDockerStatusButtonClick}
              style={{ marginTop: 4, marginLeft: 8 }}
              disabled={cannotOpenDockerStatus}
            >
              Show Docker Status
            </Button>
          )}
          <Button size="small" onClick={onShowAllLogsButtonClick} style={{ marginTop: 4, marginLeft: 8 }}>
            Show all logs
          </Button>
        </EnvironmentCardLine>
      </CardContent>
      <CardActions>
        <IconButton
          aria-label="close"
          color="info"
          size="large"
          style={{ position: "absolute", top: 1, right: 1 }}
          onClick={onCloseButtonClick}
        >
          <CancelIcon />
        </IconButton>
        <Button variant="outlined" startIcon={<EditIcon />} onClick={onEditButtonClick} disabled={cannotEditOrTerminate}>
          Edit
        </Button>
        <Button
          variant="outlined"
          startIcon={<ContentCopyIcon />}
          onClick={onReplicateFromButtonClick}
          disabled={cannotEditOrTerminate}
        >
          Replicate From
        </Button>
        {!isScalable &&
          ((content.terminationDate !== undefined && content.stage <= 7 && (
            <Button variant="outlined" startIcon={<AddIcon />} onClick={onReviveButtonClick} disabled={cannotRevive}>
              Revive
            </Button>
          )) || (
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={onTerminateButtonClick}
              disabled={cannotEditOrTerminate}
            >
              Terminate
            </Button>
          ))}
        {!isProd && (
          <Button
            variant="outlined"
            color="error"
            startIcon={<RestartAltIcon />}
            onClick={onResetButtonClick}
            disabled={cannotEditOrTerminate}
          >
            Reset Data
          </Button>
        )}
        {!isProd && !isScalable && (
          <Button variant="outlined" color="error" startIcon={<DeleteOutlineIcon />} onClick={onClearSDCacheButtonClick}>
            Clear SD Cache
          </Button>
        )}
        <div style={{ flexGrow: 1 }} />
        {!isScalable && (
          <Button onClick={onSetDefaultButtonClick} disabled={content.isDefault || cannotEditOrTerminate}>
            Set as Default
          </Button>
        )}
      </CardActions>
      <YesNoCancelPopup
        visible={syncConfirmVisible}
        title="Sync Registry Files"
        message={`Are you sure you want to sync all registry files from DEV to <b>${content.name}</b>?<br/>
        This will update the Registry File, Compiled Data Schema, and TFID Types File to match the DEV versions.`}
        onNo={handleSyncConfirmNo}
        onYes={handleSyncConfirmYes}
      />
      {syncing && <LoadingOverlay />}
      <AgentTokenPopup
        visible={agentTokenPopupVisible}
        baseURL={content.baseURL}
        onClose={() => setAgentTokenPopupVisible(false)}
      />
    </DraggableCard>
  );
};
